import { useState } from "react";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import { ScoreRing } from "@/components/ScoreBadge";
import { Swords, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import { getRecaptchaToken } from "@/lib/recaptcha";

const CAT_LABEL = {
  fr: {
    generic_green: "Générique vert", climate_neutral: "Neutralité climat",
    recycled: "Recyclé", biodegradable: "Biodégradable", natural: "Naturel",
    bio: "Bio", eco_responsible: "Éco-responsable", carbon_offset: "Compensation carbone",
    sustainable: "Durable",
  },
  en: {
    generic_green: "Generic green", climate_neutral: "Climate neutral",
    recycled: "Recycled", biodegradable: "Biodegradable", natural: "Natural",
    bio: "Organic", eco_responsible: "Eco-friendly", carbon_offset: "Carbon offset",
    sustainable: "Sustainable",
  },
};

function catLabel(c, lang) {
  return (CAT_LABEL[lang] || CAT_LABEL.en)[c] || c.replace(/_/g, " ");
}

function ScoreCard({ label, data, verdict }) {
  return (
    <div className="hairline p-6" data-testid={`compare-card-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="eyebrow">{label}</p>
        <p className="text-xs text-muted-foreground truncate max-w-[200px]">{data.url}</p>
      </div>
      <div className="flex items-center gap-6">
        <ScoreRing score={data.compliance_score} size={130} stroke={12} />
        <div>
          <div className="flex gap-2 mb-2">
            <span className="tag-red px-2 py-1 rounded-full text-xs">{data.red_count} rouges</span>
            <span className="tag-orange px-2 py-1 rounded-full text-xs">{data.orange_count} oranges</span>
          </div>
          <p className="text-xs text-muted-foreground">{data.pages_scanned} pages · {data.grade}</p>
          <p className={`text-sm mt-2 font-medium ${verdict === "better" ? "text-emerald-700" : verdict === "worse" ? "text-red-600" : "text-muted-foreground"}`}>
            {verdict === "better" && "🏆 Meilleur"}{verdict === "worse" && "⚠ En retard"}{verdict === "tied" && "Égalité"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ComparePage() {
  const { t, lang } = useLang();
  const [mine, setMine] = useState("");
  const [theirs, setTheirs] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [website, setWebsite] = useState(""); // honeypot

  const run = async (e) => {
    e.preventDefault();
    if (!mine || !theirs) { toast.error(t("compare.err.miss")); return; }
    setLoading(true); setData(null);
    try {
      const recaptcha_token = await getRecaptchaToken("compare");
      const { data: d } = await api.post(`/compare?competitor=${encodeURIComponent(theirs)}`, { url: mine, website, recaptcha_token });
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("compare.err.fail"));
    } finally {
      setLoading(false);
    }
  };

  const verdict = data ? (data.score_delta > 0 ? "mine" : data.score_delta < 0 ? "theirs" : "tie") : null;

  return (
    <div className="App min-h-screen brand-mesh">
      <Header variant="app" />
      <main className="max-w-6xl mx-auto px-6 py-12" data-testid="compare-page">
        <div className="flex items-center gap-3 mb-2">
          <Swords className="w-6 h-6 text-brand-blue" />
          <h1 className="font-serif text-3xl md:text-4xl">{t("compare.title")}</h1>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-8">{t("compare.sub")}</p>

        <form onSubmit={run} className="hairline p-6 grid md:grid-cols-[1fr_1fr_auto] gap-3 mb-10">
          <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)}
            style={{ position: "absolute", left: "-9999px" }} autoComplete="off" tabIndex="-1" aria-hidden="true" data-testid="compare-honeypot" />
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">{t("compare.mine")}</label>
            <input value={mine} onChange={e => setMine(e.target.value)} placeholder="https://votre-site.fr" className="input-brand" data-testid="compare-mine-input" required />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">{t("compare.theirs")}</label>
            <input value={theirs} onChange={e => setTheirs(e.target.value)} placeholder="https://concurrent.fr" className="input-brand" data-testid="compare-theirs-input" required />
          </div>
          <div className="flex items-end">
            <button type="submit" className="btn-primary" disabled={loading} data-testid="compare-submit">
              {loading ? t("compare.loading") : t("compare.cta")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>

        {data && (
          <div data-testid="compare-results">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <ScoreCard label={t("compare.mine")} data={data.mine} verdict={verdict === "mine" ? "better" : verdict === "theirs" ? "worse" : "tied"} />
              <ScoreCard label={t("compare.theirs")} data={data.theirs} verdict={verdict === "theirs" ? "better" : verdict === "mine" ? "worse" : "tied"} />
            </div>

            <div className="hairline p-6 mb-6" data-testid="compare-verdict">
              <p className="eyebrow mb-2">{t("compare.verdict")}</p>
              <p className="text-2xl font-serif">
                {verdict === "mine" && `🏆 ${t("compare.you_lead")} +${data.score_delta}`}
                {verdict === "theirs" && `⚠ ${t("compare.they_lead")} ${-data.score_delta}`}
                {verdict === "tie" && `⚖ ${t("compare.tie")}`}
              </p>
            </div>

            {data.categories.length > 0 && (
              <div className="hairline p-6">
                <p className="eyebrow mb-4">{t("compare.deltas")}</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-muted-foreground border-b border-border">
                      <th className="text-left py-2">{t("compare.category")}</th>
                      <th className="text-center py-2">{t("compare.mine")}</th>
                      <th className="text-center py-2">{t("compare.theirs")}</th>
                      <th className="text-center py-2">Δ {t("compare.red")}</th>
                      <th className="text-center py-2">Δ {t("compare.orange")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.categories.map(c => (
                      <tr key={c.category} className="border-b border-border/50" data-testid={`compare-cat-${c.category}`}>
                        <td className="py-3">{catLabel(c.category, lang)}</td>
                        <td className="text-center">
                          <span className="tag-red px-2 py-0.5 rounded-full text-xs mr-1">{c.mine.red}</span>
                          <span className="tag-orange px-2 py-0.5 rounded-full text-xs">{c.mine.orange}</span>
                        </td>
                        <td className="text-center">
                          <span className="tag-red px-2 py-0.5 rounded-full text-xs mr-1">{c.theirs.red}</span>
                          <span className="tag-orange px-2 py-0.5 rounded-full text-xs">{c.theirs.orange}</span>
                        </td>
                        <td className="text-center font-mono">
                          {c.delta_red === 0 ? <Minus className="w-3 h-3 inline text-muted-foreground" /> : c.delta_red > 0 ?
                            <span className="text-emerald-700 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" />+{c.delta_red}</span> :
                            <span className="text-red-600 inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" />{c.delta_red}</span>}
                        </td>
                        <td className="text-center font-mono">
                          {c.delta_orange === 0 ? <Minus className="w-3 h-3 inline text-muted-foreground" /> : c.delta_orange > 0 ?
                            <span className="text-emerald-700 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" />+{c.delta_orange}</span> :
                            <span className="text-red-600 inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" />{c.delta_orange}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-muted-foreground mt-4">{t("compare.note")}</p>
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
